'use client';

import { Button } from '@ui/core/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@ui/core/dialog';
import { useDialog } from '@ui/core/use-global-dialog';
import React, { useCallback, useEffect, useRef } from 'react';

interface RemoveAttachmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

export const RemoveAttachmentDialog: React.FC<RemoveAttachmentDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const dialog = useDialog();
  const isDialogOpenRef = useRef(false);

  // Memoize the close handler to avoid recreating it
  const handleClose = useCallback(() => {
    dialog.close();
    isDialogOpenRef.current = false;
    onClose();
  }, [dialog, onClose]);

  // Memoize the confirm handler to avoid recreating it
  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm();
      dialog.close();
      isDialogOpenRef.current = false;
      onClose();
    } catch (error) {
      console.error('Error removing attachment:', error);
      dialog.close();
      isDialogOpenRef.current = false;
      onClose();
    }
  }, [dialog, onConfirm, onClose]);

  // Handle opening the dialog when isOpen becomes true
  useEffect(() => {
    if (isOpen && !isDialogOpenRef.current) {
      isDialogOpenRef.current = true;
      dialog.open({
        content: (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-800">
                Remove attachment?
              </DialogTitle>
            </DialogHeader>

            <DialogFooter className="[&>*[data-primary='true']]:flex-grow [&>*[data-primary='true']]:min-w-[120px] [&>*[data-primary='true']]:whitespace-normal">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isLoading}
              >
                Back
              </Button>
              <Button
                type="button"
                data-primary={true}
                onClick={handleConfirm}
                disabled={isLoading}
                className="bg-red-700 hover:bg-red-800 border-red-700 text-white"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </Button>
            </DialogFooter>
          </div>
        ),
        preventOutsideClose: true,
        className: 'sm:max-w-[400px]',
      });
    }
  }, [isOpen, dialog, handleClose, handleConfirm, isLoading]);

  // Handle closing the dialog when isOpen becomes false
  useEffect(() => {
    if (!isOpen && isDialogOpenRef.current) {
      dialog.close();
      isDialogOpenRef.current = false;
    }
  }, [isOpen, dialog]);

  return null;
};
